import { Temporal } from '@js-temporal/polyfill';
import { DisruptionMessage, LatLng } from '@ridango/core';
import PlainTime = Temporal.PlainTime;

export enum ShapeType {
    ROUNDED = 'ROUNDED',
    SQUARED = 'SQUARED',
    RECTANGLE = 'RECTANGLE'
}

export interface Route extends BaseRoute {
    agencyName: string;
    gtfsId: string;
}

export interface RouteGroups {
    name: string;
    routes: Route[];
}

export interface DetailedRoute extends BaseRoute {
    agency: Agency;
    type: number;
    desc: string;
    url: string;
    textColor: string;
    bikesAllowed: number;
    sortOrder: number;
    brandingUrl: string;
    mode: TransportMode;
    disruptionOnRoute?: boolean;
    disruptionMessages?: DisruptionMessage[];
}

export interface BaseRoute {
    id: string;
    shortName: string;
    longName?: string;
    mode: TransportMode;
    operatorCode?: string | null;
    operatorName?: string | null;
    color?: string;
    icon?: {
        color: string;
        textColor: string | null;
        shape: ShapeType;
    };
}

export interface StopMarker {
    id?: string;
    name: string;
    code: string;
    desc?: string;
    dist?: number;
    latlng: LatLng;
    departureClockTime?: string | Date;
    arrivalClockTime?: string | Date;
}

export enum PolyLineStyle {
    SOLID = 'solid',
    DASHED = 'dashed'
}

export interface DetailedStop extends Stop {
    desc: string;
    zoneId: string;
    url: string;
    locationType: number;
    stationId: string;
    wheelchairBoarding: number;
    direction: string;
    pickupType: number;
    dropOffType: number;
}

export interface Stop {
    id: string;
    code: string;
    dist?: number;
    desc?: string;
    lat: number;
    lon: number;
    name: string;
    disruptionMessages?: DisruptionMessage[];
}

export interface TripDetails {
    id: string;
    routeId: string;
    serviceId: string;
    name: string;
    headsign: string;
    tripHeadsign: string;
    routeShortName: string;
    shapeId: string;
    wheelchairAccessible: number;
    bikesAllowed: number;
}

export interface StopTime {
    pattern: Pattern;
    times: Time[];
}

export interface Departure {
    time: Time;
    route: DetailedRoute;
    isDateNotInPast?: boolean;
}

export interface Time {
    stopId: string;
    stopIndex: number;
    stopCount: number;
    scheduledArrival: number | string;
    scheduledDeparture: number | string;
    realtimeArrival: number | string;
    realtimeDeparture: number | string;
    arrivalDelay: number;
    departureDelay: number;
    timepoint: boolean;
    realtime: boolean;
    realtimeState: RealtimeStatus;
    serviceDay: number;
    tripId: string;
    blockId: string;
    headsign: string;
    stopPoint?: string;
    canceled?: boolean;
    pickupType?: PickupType;
    dropOffType?: DropOffType;
    tripDirection?: number;
}

export enum PickupType {
    REGULAR = 0,
    NO_PICKUP = 1,
    PHONE_AGENCY = 2,
    COORDINATE_WITH_DRIVER = 3
}

export enum DropOffType {
    REGULAR = 0,
    NO_DROP_OFF = 1,
    PHONE_AGENCY = 2,
    COORDINATE_WITH_DRIVER = 3
}

export interface TripDetailsTime extends Time {
    mode?: TransportMode;
    stop: Stop;
    departureTime?: PlainTime;
    scheduledTime?: PlainTime;
    scheduledTimeExceedingAllowedThreshold?: boolean;
}

export interface Pattern {
    id: string;
    desc: string;
    tripHeadsign?: string;
    routeId: string;
    routeDetails?: DetailedRoute;
}

export interface Geometry {
    points: string;
    length: number;
}

export interface Agency {
    id: string;
    name: string;
    url: string;
    timezone: string;
    lang: string;
    phone: string;
    fareUrl: string;
    brandingUrl: string;
}

export interface Itinerary {
    duration: number;
    startTime: number;
    endTime: number;
    fare: ItineraryFare;
    realTime?: boolean;
    walkTime: number;
    transitTime: number;
    waitingTime: number;
    walkDistance: number;
    walkLimitExceeded: boolean;
    generalizedCost: number;
    elevationLost: number;
    elevationGained: number;
    transfers: number;
    legs: Leg[];
    tooSloped: boolean;
    arrivedAtDestinationWithRentedBicycle: boolean;
    changes?: number;
    journeyEndArrivalDelay?: number;
    journeyStartDepartureDelay?: number;
    isMostOptimal?: boolean;
    journeyKey?: string;
}
export interface ItineraryFare {
    priceZoneList?: string;
    fare: Fare;
}

export interface LegProduct {
    amount: number;
    currency: string;
}
export interface Fare {
    regular: PriceClass;
}

export interface PriceClass {
    cents: number;
    currency: Currency;
}
export interface Currency {
    currency: string;
    defaultFractionDigits: number;
    currencyCode: string;
    symbol: string;
}

export interface Leg {
    startTime: number;
    endTime: number;
    departureDelay: number;
    arrivalDelay: number;
    realTime: boolean;
    distance: number;
    generalizedCost: number;
    pathway: boolean;
    mode: TransportMode;
    transitLeg: boolean;
    route: string;
    agencyTimeZoneOffset: number;
    interlineWithPreviousLeg: boolean;
    from: PlannerStop;
    to: PlannerStop;
    legGeometry: LegGeometry;
    steps: Step[];
    rentedBike?: boolean;
    walkingBike?: boolean;
    duration: number;
    agencyName?: string;
    agencyUrl?: string;
    routeColor?: string;
    price?: number;
    currency?: string;
    icon?: {
        color: string;
        textColor: string | null;
        shape: ShapeType;
    };
    routeType?: number;
    routeId?: string;
    routeTextColor?: string;
    headsign?: string;
    operatorCode?: string | null;
    operatorName?: string | null;
    agencyId?: string;
    tripId?: string;
    serviceDate?: Date;
    intermediateStops?: PlannerStop[];
    routeShortName?: string;
    routeLongName?: string;
    disruptionMessages?: DisruptionMessage[];
    canceled?: boolean;
    tripDirection?: number;
    arrIsTimingPoint?: boolean;
    depIsTimingPoint?: boolean;
    alerts?: LegAlert[];
}

interface LegAlert {
    effect?: string;
    messages?: any[];
}

export interface PlannerStop {
    name: string;
    lon: number;
    lat: number;
    departure?: number;
    vertexType: VertexType;
    stopId?: string;
    stopCode?: string;
    arrival?: number;
    zoneId?: string;
    stopIndex?: number;
    stopSequence?: number;
    platformCode?: string;
}

export interface LegGeometry {
    points: string;
    length: number;
}

export interface Step {
    distance: number;
    relativeDirection: string;
    streetName: string;
    absoluteDirection: string;
    stayOn: boolean;
    area: boolean;
    bogusName: boolean;
    lon: number;
    lat: number;
    elevation: string;
    walkingBike: boolean;
    exit?: string;
}

export interface TripClickEvent {
    tripId: string;
    routeId: string;
    userDepartStopId: string;
    filterDateTime?: string;
    fromTime?: string;
    shortName?: string;
    filterType?: 'leaveAfter' | 'arriveBy';
}

export interface ExploreMapRoute {
    polyline: string;
    polylineColor: string;
    stops: StopMarker[];
    userDepartureBusStop?: Stop;
}

export interface JourneyMapPlan {
    userLocation: LatLng;
    journeyOriginLocation: LatLng;
    journeyDestinationLocation: LatLng;
    legs: JourneyMapPlanLeg[];
}

export interface JourneyMapPlanLeg {
    markers: StopMarker[];
    polyline: string;
    polylineStyle: PolyLineStyle;
    color: string;
}

export interface RoutePatternStops {
    pattern: Pattern;
    stops: Stop[];
    route: DetailedRoute;
}

export interface Plan {
    error?: PlannerError;
    plan: {
        date?: number;
        from: PlannerStop;
        to: PlannerStop;
        itineraries: Itinerary[];
    };
}

// http://dev.opentripplanner.org/apidoc/2.3.0/json_PlannerError.html
export interface PlannerError {
    id: number;
    message: PlannerErrorMessage;
    msg: string;
}

// http://dev.opentripplanner.org/apidoc/2.3.0/json_Message.html
export enum PlannerErrorMessage {
    NO_TRANSIT_TIMES = 'NO_TRANSIT_TIMES',
    LOCATION_NOT_ACCESSIBLE = 'LOCATION_NOT_ACCESSIBLE',
    GEOCODE_FROM_NOT_FOUND = 'GEOCODE_FROM_NOT_FOUND',
    GEOCODE_TO_NOT_FOUND = 'GEOCODE_TO_NOT_FOUND',
    PATH_NOT_FOUND = 'PATH_NOT_FOUND',
    INTERNAL_ERROR = 'INTERNAL_ERROR'
}

export enum VertexType {
    Normal = 'NORMAL',
    Transit = 'TRANSIT'
}

export enum TransportMode {
    Bus = 'BUS',
    Ferry = 'FERRY',
    Rail = 'RAIL',
    Tram = 'TRAM',
    Trolleybus = 'TROLLEYBUS',
    Walk = 'WALK',
    Flexible = 'FLEXIBLE'
}

export enum queryParameter {
    minTransferTime = 'minTransferTime',
    walkSpeed = 'walkSpeed',
    mode = 'mode'
}

export const JourneyPlannerItineraryTransportMode = {
    ...TransportMode,
    Wait: 'WAIT'
};

export enum RealtimeStatus {
    SCHEDULED = 'SCHEDULED',
    UPDATED = 'UPDATED',
    CANCELED = 'CANCELED',
    ADDED = 'ADDED',
    MODIFIED = 'MODIFIED'
}

export enum JourneyDirection {
    Origin = 'origin',
    Destination = 'destination'
}

export interface SearchRecentlyJourney {
    origin?: {
        name?: string;
        coordinates?: LatLng;
        useCurrentLocation?: boolean;
        useLocationFromMap?: boolean;
        municipalityName?: string;
    };
    destination?: {
        name?: string;
        coordinates?: LatLng;
        useCurrentLocation?: boolean;
        useLocationFromMap?: boolean;
        municipalityName?: string;
    };
}

export interface VehiclePosition {
    latitude: number;
    longitude: number;
}

export interface Vehicle {
    trip?: { tripId: string };
    position: VehiclePosition;
}

export interface BusPosition {
    segmentIndex: number;
    progressPercent: number;
    isAtStop: boolean;
    atStopIndex?: number;
}
